import { formatDistanceToNow } from 'date-fns';

import type { NotificationResponse } from '@api';
import type { NotificationsFeedSnapshot } from './types';

export const EMPTY_NOTIFICATIONS_FEED_SNAPSHOT: NotificationsFeedSnapshot = {
  items: [],
  total: 0,
  status: 'idle',
  organizationId: null,
  lastReadAt: null,
};

const READ_STORAGE_PREFIX = 'sp-outreach-ui.Notifications-lastRead';

export const getLastReadStorageKey = (organizationId: string) =>
  `${READ_STORAGE_PREFIX}.${organizationId}`;

export const loadLastReadAt = (organizationId: string | null) => {
  if (!organizationId) return null;

  return localStorage.getItem(getLastReadStorageKey(organizationId));
};

export const saveLastReadAt = (organizationId: string, lastReadAt: string) => {
  localStorage.setItem(getLastReadStorageKey(organizationId), lastReadAt);
};

export const parseNotificationDate = (date: string) => {
  const parsed = new Date(date.replace(/\s+/, 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getRelativeTime = (date: string): string => {
  const parsed = parseNotificationDate(date);
  if (!parsed) return date;

  return formatDistanceToNow(parsed, { addSuffix: true });
};

export const getNotificationProgress = (notification: NotificationResponse) => {
  const total = notification.total_count ?? 0;
  if (total <= 0) return 0;

  const processed = notification.processed_count ?? 0;
  return Math.min(100, Math.round((processed / total) * 100));
};

export const getNotificationStatusColor = (status: string): SlugColorType => {
  if (status === 'failed' || status === 'completed_with_errors') {
    return 'danger';
  }

  if (status === 'completed') return 'success';
  if (status === 'running') return 'warning';

  return 'gray';
};

export const getNotificationJobId = (
  notification: NotificationResponse
): string | null => {
  const dataJobId = notification.data?.job_id;
  if (typeof dataJobId === 'string') return dataJobId;

  return notification.resource_id;
};

export const getNotificationSignature = (
  notification: NotificationResponse | undefined
) => {
  if (!notification) return '';

  return [
    notification.id,
    notification.status,
    notification.processed_count ?? '',
    notification.failed_count ?? '',
    notification.total_count ?? '',
  ].join('|');
};

export const getUnreadCount = (
  items: NotificationResponse[],
  lastReadAt: string | null
) => {
  if (!items.length) return 0;
  if (!lastReadAt) return items.length;

  const lastReadTime = parseNotificationDate(lastReadAt)?.getTime();
  if (lastReadTime == null) return items.length;

  return items.filter(notification => {
    const createdAt = parseNotificationDate(notification.created_at)?.getTime();
    return createdAt != null && createdAt > lastReadTime;
  }).length;
};
